import React, { useCallback } from 'react'
import { connect as connectStaticText } from '../../../../StaticText/connect'
import { StaticTextProvider, useTranslatedText } from '../../../../StaticText/StaticTextContext'
import Button, { TYPES as BUTTON_TYPES } from '../../../../Button/index'
import type { StaticTextMap } from '../../../../../services/community/types'

interface CommunityErrorFallbackInnerProps {
  resetErrorBoundary?: () => void
}

interface CommunityErrorFallbackProps extends CommunityErrorFallbackInnerProps {
  staticText?: StaticTextMap
}

/**
 * Inner: lives inside the Outer's <StaticTextProvider>, so useTranslatedText
 * resolves against the `communityErrorFallback` slice. No `staticText` prop —
 * UI copy is resolved via hooks against the surrounding provider.
 */
function CommunityErrorFallbackInner ({
  resetErrorBoundary,
}: CommunityErrorFallbackInnerProps) {
  const handleRetry = useCallback(() => {
    if (resetErrorBoundary) resetErrorBoundary()
  }, [resetErrorBoundary])

  const heading = useTranslatedText({ keys: ['heading'] })
  const message = useTranslatedText({ keys: ['message'] })
  const retryLabel = useTranslatedText({ keys: ['retryLabel'] })
  const retryAria = useTranslatedText({ keys: ['retryAria'] })

  return (
    <div className="community-error-fallback" role="alert">
      <span className="community-error-fallback__icon" aria-hidden="true" />
      <h3 className="community-error-fallback__heading">{heading}</h3>
      <p className="community-error-fallback__message">{message}</p>
      {resetErrorBoundary ? (
        <Button
          type={BUTTON_TYPES.GHOST}
          className="community-error-fallback__retry"
          onClick={handleRetry}
          aria-label={retryAria}
        >
          {retryLabel}
        </Button>
      ) : null}
    </div>
  )
}

/**
 * Outer wrapper: receives `staticText` from connectStaticText and provides
 * it to the subtree so Inner's `useTranslatedText` hooks resolve against the
 * `communityErrorFallback` slice. Mirrors the Oracle Outer/Inner pattern.
 *
 * Section-level error fallback for granular error boundaries. Unlike the
 * page-level withCommunityErrorBoundary (which shows a 500 ErrorPage), this
 * renders a compact inline message with a retry button so a crash in one
 * section (e.g. playlists, archetypes) does not take down the entire profile.
 */
function CommunityErrorFallback ({
  resetErrorBoundary,
  staticText,
}: CommunityErrorFallbackProps) {
  return (
    <StaticTextProvider staticText={staticText}>
      <CommunityErrorFallbackInner resetErrorBoundary={resetErrorBoundary} />
    </StaticTextProvider>
  )
}

export { CommunityErrorFallback }
export default connectStaticText({ storeKey: 'communityErrorFallback' })(
  React.memo(CommunityErrorFallback),
)
